import React from 'react';
import { useDispatch } from 'react-redux';
import parseQS from 'helpers/parseQS';
import { useTranslate } from 'hooks/useTranslate';
import { useLocation, Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from 'assets/icons/search_icon.svg?react';
import FilterIcon from 'assets/icons/iconoir_sort-up.svg?react';
import useDataListFactory from 'services/dataList';
import { IS_SNAP } from 'variables/main';
import config from 'config';
import { resolveMedia } from 'actions/media';
import { CONSULTANT_SERVICES, CONSULTANT_REVIEWS } from 'variables/consultants';
import Bg1 from './../../Vacancy/assets/bg1.svg?react';
import Smartphone from './../assets/smartphone.svg?react';
import Bird from './../assets/bird.svg?react';
import Robot from './../assets/robot.svg?react';

const Helmet = React.lazy(() => import('components/Helmet'));
const Drawer = React.lazy(() => import('components/Drawer'));
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const Cards = React.lazy(() => import('components/Card'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));
const LinearProgress = React.lazy(() => import('components/LinearProgress'));
const Questions = React.lazy(() => import('pages/Consultant/components/FAQ'));

const ServiceBlock = ({ service }) => {
  const t = useTranslate('Consultants');
  return (
    <div>
      <Typography variant="h3" sx={{ fontWeight: 600, mb: 2 }}>{t(`${service.key}Title`)}</Typography>
      <Typography sx={{ fontWeight: 600, mb: 2 }}>{t(`${service.key}Description`)}</Typography>
      <ol>
        {service.stepKeys.map((k) => (
          <li key={k} style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{t(k)}</li>
        ))}
      </ol>
      {service.outcomeKeys.map((k) => (
        <Typography key={k} sx={{ mb: 1 }}>{t(k)}</Typography>
      ))}
    </div>
  );
};

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Background = styled('section', {
  shouldForwardProp: (prop) => prop !== 'pb' && prop !== 'hero'
})(({ theme, pb, hero }) => ({
  backgroundColor: hero ? '#fdfef9' : '#eaf1de',
  paddingBottom: pb || 0,
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 0
  }
}));

const HomeProfessionsWrapper = styled('div')(({ theme, mb, pb }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  marginBottom: mb || 0,
  paddingBottom: pb || 40,
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    paddingBottom: 30
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  marginTop: 50,
  fontSize: 50,
  lineHeight: '57px',
  maxWidth: 780,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 25
  }
}));

const DescriptionBold = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  fontSize: 24,
  lineHeight: '27px',
  fontWeight: 600,
  marginBottom: 20,
  [theme.breakpoints.down('sm')]: {
    fontSize: 21,
    lineHeight: '27px'
  }
}));

const DescriptionNormal = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  fontSize: 16,
  lineHeight: '22px',
  marginBottom: 10,
  marginTop: 10
}));

const ActionsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 40,
  [theme.breakpoints.down('md')]: {
    marginBottom: 20
  }
}));

const TextFieldStyled = styled(TextField)(() => ({
  borderRadius: 8
}));

const FilterIconStyled = styled(FilterIcon)(() => ({
  marginRight: 10,
  width: 43,
  height: 43,
  cursor: 'pointer'
}));

const ButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '0 auto',
  borderRadius: 8
}));

const ClearIconStyled = styled(ClearIcon)(({ theme }) => ({
  cursor: 'pointer',
  marginRight: 10,
  color: theme.palette.textPrimary.main
}));

const ListImageComponent = styled('img')(() => ({
  width: '100%',
  maxWidth: 370,
  marginBottom: 40
}));

const FlexDescription = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 200,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 15
  }
}));

const BackgroundGallery = styled('section')(() => ({
  backgroundColor: '#005919'
}));

const FlexGallery = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 3fr))',
  gap: 20,
  marginTop: 10,
  marginBottom: 10,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}));

const GalleryText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontSize: 16,
  lineHeight: '22px',
  marginTop: 10,
  textAlign: 'center'
}));

const GalleryItem = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  lineHeight: '29px',
  fontFamily: 'Unbounded',
  fontWeight: 600,
  color: theme.palette.textPrimary.main
}));

const Text = styled(Typography)(({ theme, color }) => ({
  fontSize: 16,
  lineHeight: '22px',
  color: color || theme.palette.textPrimary.main,
  marginTop: 10,
  marginBottom: 24
}));

const Headline = styled(Typography)(({ theme }) => ({
  marginBottom: 24,
  fontSize: 32,
  [theme.breakpoints.down('md')]: {
    marginBottom: 30,
    fontSize: 28
  }
}));

const Flex = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 24,
  marginBottom: 40,
  position: 'relative',
  zIndex: 100,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 24,
    marginBottom: 24
  }
}));

const FlexItem = styled('div')(({ theme }) => ({
  flex: 1,
  borderRadius: 18,
  padding: 24,
  backgroundColor: '#fff',
  border: '1px solid #B9F833'
}));

const Bg1Styled = styled(Bg1)(({ theme }) => ({
  position: 'absolute',
  left: '35%',
  top: -115,
  width: 1000,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BirdStyled = styled(Bird)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: -155,
  width: 200,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const SmartphoneStyled = styled(Smartphone)(({ theme }) => ({
  position: 'absolute',
  left: 25,
  bottom: 85,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const RobotStyled = styled(Robot)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: -340,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const PageLayout = ({
  endPoint,
  translate,
  innerPage,
  breadcrumbs: breadcrumbsProp,
  Filters,
  CardTemplate,
  title,
  meta,
  loading: loadingProp,
  useFavorites,
  getRedirectUrl,
  externalLink,
  borderedCard,
  ChosenFilters
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [initFilters, setInitFilters] = React.useState();
  const [mediaMap, setMediaMap] = React.useState({});

  const [qsPage] = React.useState(() => {
    if (!IS_SNAP) return null;
    const pageNumber = (location?.pathname || '').split('/').pop() || null;
    if (pageNumber && !Number.isNaN(Number(pageNumber))) {
      return Number(pageNumber);
    }
    return null;
  });

  const { actions, data, loading, page, perPage, count, filters, sort } = useDataList(endPoint, {
    page: qsPage || 1
  });

  const [open, setOpen] = React.useState(false);

  const t = useTranslate(translate);

  const handleScrollToList = React.useCallback(() => {
    const yOffset = -80;
    const element = document.getElementById('cardsList');
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const handleOpenDrawer = React.useCallback(() => setOpen(true), []);

  const handleCLoseDrawer = React.useCallback(() => setOpen(false), []);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeSearch = React.useCallback(
    (e) => {
      actions.onSearchChange(e.target.value);
    },
    [actions]
  );

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleChangeSearch(e);
      }
    },
    [handleChangeSearch]
  );

  const handleClearSearch = React.useCallback(() => {
    actions.clearData(true);
  }, [actions]);

  const cardTemplate = React.useCallback(
    (item) => (CardTemplate ? <CardTemplate t={t} item={item} /> : null),
    [CardTemplate, t]
  );

  const limitReached = React.useMemo(() => {
    if (qsPage && count <= qsPage * perPage) {
      return true;
    }
    return count <= page * perPage;
  }, [count, page, perPage, qsPage]);

  React.useEffect(() => {
    const ids = CONSULTANT_REVIEWS.map((r) => r.imageGallery);
    let cancelled = false;
    dispatch(resolveMedia(ids)).then((map) => {
      if (!cancelled) setMediaMap(map || {});
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  const breadcrumbs = React.useMemo(() => breadcrumbsProp, [breadcrumbsProp]);

  React.useEffect(() => {
    if (initFilters) return;
    setInitFilters(true);
    handleChangeSearch({
      target: { value: parseQS(location.search).search || '' }
    });
  }, [handleChangeSearch, initFilters, location.search]);

  return (
    <>
      <Helmet meta={meta} />
      <MainLayout>
        <Header />

        <Background hero>
          <HomeProfessionsWrapper>
            <Bg1Styled />

            <Breadcrumbs breadcrumbs={breadcrumbs} />
            {loadingProp ? null : (
              <>
                <Title variant="h1">{title || t('Title')}</Title>

                <FlexDescription>
                  <ListImageComponent
                    src={`${config.imageSource}/151123fd-b062-45f2-dc7a-5a8bf7251e00/public`}
                    alt="Кар'єрне планування"
                  />

                  <div style={{ marginTop: -80 }}>
                    <DescriptionBold variant="h2">
                      {t('Block3Large')}
                    </DescriptionBold>

                    <DescriptionNormal variant="body1">
                      {t('Block3Small')}
                    </DescriptionNormal>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleScrollToList}
                      sx={{ marginTop: 2, borderRadius: '8px' }}>
                      {t('GetConsultation')}
                    </Button>
                  </div>
                </FlexDescription>
              </>
            )}
          </HomeProfessionsWrapper>
        </Background>

        <Background pb={50}>
          <BackgroundGallery>
            <HomeProfessionsWrapper>
              <FlexGallery>
                {CONSULTANT_REVIEWS.map((review) => {
                  const src = mediaMap[review.imageGallery];
                  const description = t(review.descriptionKey);
                  return (
                    <GalleryItem key={review.id}>
                      <img src={src ? `${src}/public` : ''} alt={description} />
                      <GalleryText>{description}</GalleryText>
                    </GalleryItem>
                  );
                })}
              </FlexGallery>
            </HomeProfessionsWrapper>
          </BackgroundGallery>
        </Background>

        <Background>
          <HomeProfessionsWrapper>
            <Subtitle>{t('Block2Large')}</Subtitle>

            <Text>{t('Block2Small')}</Text>

            <ActionsWrapper id="cardsList">
              <FilterIconStyled onClick={handleOpenDrawer} />
              <TextFieldStyled
                value={filters?.name || ''}
                onChange={handleChangeSearch}
                onKeyDown={onKeyDown}
                placeholder={t('Search')}
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        {filters?.name ? <ClearIconStyled onClick={handleClearSearch} /> : null}
                        <SearchIcon />
                      </>
                    )
                  }
                }}
              />
            </ActionsWrapper>

            {ChosenFilters ? <ChosenFilters filters={filters} actions={actions} /> : null}

            <Cards
              list={data}
              template={cardTemplate}
              endPoint={innerPage}
              useFavorites={useFavorites}
              getRedirectUrl={getRedirectUrl}
              externalLink={externalLink}
              borderedCard={borderedCard}
            />

            {data && data.length === 0 ? <Typography>{t('NoData')}</Typography> : null}

            {loading || loadingProp || !data ? <LinearProgress /> : null}

            {limitReached ? null : (
              <>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  onClick={handleChangePage}
                  aria-label={t('LoadMore')}>
                  {t('LoadMore')}
                </ButtonStyled>
                <Link to={`/${innerPage}/page/${page + 1}`} style={{ display: 'none' }}>
                  {t('LoadMore')}
                </Link>
              </>
            )}
          </HomeProfessionsWrapper>
        </Background>

        <Background pb={50}>
          <BackgroundGallery>
            <HomeProfessionsWrapper>
              <Text color={'#ffffff'}>{t('Block1Large')}</Text>
              <Text color={'#B9F833'}>{t('Block1Small')}</Text>
            </HomeProfessionsWrapper>
          </BackgroundGallery>
        </Background>

        <Background>
          <HomeProfessionsWrapper pb={'0px'}>
            <Headline variant="h2">{t('OurServices')}</Headline>

            <Flex>
              {CONSULTANT_SERVICES.map((service) => (
                <FlexItem key={service.key}>
                  <ServiceBlock service={service} />
                </FlexItem>
              ))}
            </Flex>

            <ButtonStyled
              variant="contained"
              color="primary"
              onClick={handleScrollToList}
              sx={{ marginTop: 2, borderRadius: '8px' }}>
              {t('GetConsultation')}
            </ButtonStyled>
          </HomeProfessionsWrapper>
        </Background>

        <Background pb={'200px'}>
          <Questions />

          <SmartphoneStyled />

          <RobotStyled />

          <BirdStyled />
        </Background>

        <Footer />

        <Drawer anchor={'left'} open={open} onClose={handleCLoseDrawer}>
          <Filters actions={actions} filters={filters} sort={sort} />
        </Drawer>
      </MainLayout>
    </>
  );
};

export default PageLayout;
